<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		real_id,
	} from '../../../../helpers/interface_helpers';
	//Design system components
	import { Button, Select, Text, toast, Slider, Alert } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	//Components
	import AwayModeActiveAlert from 'src/components/Alerts/AwayModeActiveAlert.svelte';

	export let device;

	const settings = devices_settings.getByRealId(device.id);
	const component = get_gen_2_component_channel_structure(device);

	// Get current status to check away mode
	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: awayModeActive = status?.[component]?.away_mode || false;

	// Cury LED indicator options
	const modeOptions = ['level', 'intensity', 'off'];
	const modeOptionsI18n = [
		$format('option_led_mode_level', { default: 'Level indicator' }),
		$format('option_led_mode_intensity', { default: 'Intensity indicator' }),
		$format('option_always_off', { default: 'Always off' }),
	];

	let selectedMode = settings?.cury?.ui?.mode || 'level';
	let selectedBrightness = settings?.cury?.ui?.brightness || 18;
	let cacheMode = selectedMode;
	let cacheBrightness = selectedBrightness;
	let loading = false;

	const changeMode = async (e) => {
		selectedMode = modeOptions[modeOptionsI18n.indexOf(e.detail)];
	};

	const changeBrightness = async (e) => {
		selectedBrightness = e.detail.value;
	};

	const sentRequests = async () => {
		if (cacheMode !== selectedMode || cacheBrightness !== selectedBrightness) {
			loading = true;

			const params = {
				id: 0,
				config: {
					ui: {
						mode: selectedMode,
						brightness: selectedBrightness,
					},
				},
			};

			try {
				const res = (await gen2Request(device, 'Cury.SetConfig', params)) as any;

				onSettingsResponse(res, device);

				if (res?.isok) {
					// Update local settings
					cacheMode = selectedMode;
					cacheBrightness = selectedBrightness;
				} else {
					// Revert on failure
					selectedMode = cacheMode;
					selectedBrightness = cacheBrightness;
				}
			} catch (err) {
				console.error('Failed to update cury LED settings:', err);
				selectedMode = cacheMode;
				selectedBrightness = cacheBrightness;
			} finally {
				loading = false;
			}
		}
	};

	const a11yProps = {
		sliderValueText: $format('current_brightness_value', {
			values: { value: selectedBrightness },
			default: `Current brightness value is ${selectedBrightness}`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: selectedBrightness },
			default: `Current brightness value is ${selectedBrightness}`,
		}),
	};
</script>

{#if awayModeActive}
	<AwayModeActiveAlert {device} />
{:else}
	<Alert
		class="mb-s"
		type="info"
		heading={$format('description_inactive_state', {
			default: 'Information to show on inactive (no user interaction) state.',
		})}
	/>
	<Text styleType="body-small" class="mb-s">
		{$format('label_mode', { default: 'Mode' })}
	</Text>
	<Select
		class="mb-s"
		value={modeOptionsI18n[modeOptions.indexOf(selectedMode)]}
		options={modeOptionsI18n}
		icon={null}
		on:change={changeMode}
		id="select-xd07t8i"
		aria-label={$format('label_mode', { default: 'Mode' })}
	/>

	{#if selectedMode === 'intensity' || selectedMode === 'level'}
		<Text styleType="body-small" class="mb-s">
			{$format('label_brightness', { default: 'Brightness' })}
		</Text>
		<Slider
			id="slider-6dheids"
			{a11yProps}
			class="mb-s"
			value={selectedBrightness}
			type="simple"
			min={0}
			max={100}
			step={1}
			title={$format('label_brightness', { default: 'Brightness' })}
			on:value_change={changeBrightness}
		/>
	{/if}

	<Button {loading} class="mt-s" size="m" width="full" on:click={sentRequests}
		>{$format('button_save')}</Button
	>
{/if}
